import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Send, X } from "lucide-react";

import { Modal } from "@/components/Modal";
import type { LiveListing } from "@/context/AppContext";
import {
  getEbayListingMessages,
  replyToEbayListingMessage,
  type ListingMessage,
} from "@/features/ebayListings";

interface Props {
  listing: LiveListing | null;
  onClose: () => void;
  onUnreadCountChange: (listingId: string, unreadCount: number) => void;
  onSuccess: (text: string) => void;
}

interface OptimisticReply {
  messageId: string;
  parentMessageId: string;
  body: string;
  sentAt: string;
}

interface Conversation {
  buyerUserId: string;
  messages: ListingMessage[];
  optimisticReplies: OptimisticReply[];
}

export function MessagesModal({
  listing,
  onClose,
  onUnreadCountChange,
  onSuccess,
}: Props) {
  const open = listing !== null;
  const [messages, setMessages] = useState<ListingMessage[]>([]);
  const [optimisticReplies, setOptimisticReplies] = useState<OptimisticReply[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [replyErrors, setReplyErrors] = useState<Record<string, string | null>>(
    {},
  );
  const [submittingFor, setSubmittingFor] = useState<string | null>(null);

  // Capture the callback in a ref so the load effect only fires when the
  // listing changes — otherwise a parent re-render hands us a fresh
  // onUnreadCountChange identity and we re-fetch in a loop.
  const onUnreadCountChangeRef = useRef(onUnreadCountChange);
  onUnreadCountChangeRef.current = onUnreadCountChange;

  useEffect(() => {
    if (!listing) {
      setMessages([]);
      setOptimisticReplies([]);
      setLoading(false);
      setError(null);
      setReplyDrafts({});
      setReplyErrors({});
      setSubmittingFor(null);
      return;
    }
    if (!listing.ebayListingId) {
      setError("Listing has no eBay item id.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getEbayListingMessages(listing.ebayListingId)
      .then((data) => {
        if (cancelled) return;
        setMessages(data.messages);
        onUnreadCountChangeRef.current(listing.id, data.unreadCount);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Failed to load messages",
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listing]);

  // Group buyer-originated conversations by senderUserId. eBay's API returns
  // every message attached to the item, so a single listing can have several
  // independent buyer threads. Each conversation owns its own composer below
  // so a reply to one buyer never affects another buyer's draft.
  const conversations: Conversation[] = useMemo(() => {
    const groups = new Map<string, Conversation>();
    const buyerIds = Array.from(
      new Set(messages.filter((m) => m.fromBuyer).map((m) => m.senderUserId)),
    );
    for (const buyerId of buyerIds) {
      groups.set(buyerId, {
        buyerUserId: buyerId,
        messages: messages.filter(
          (m) =>
            (m.fromBuyer && m.senderUserId === buyerId) ||
            (!m.fromBuyer && m.recipientUserId === buyerId),
        ),
        optimisticReplies: optimisticReplies.filter((r) => {
          const parent = messages.find(
            (m) => m.messageId === r.parentMessageId,
          );
          return parent?.senderUserId === buyerId;
        }),
      });
    }
    return Array.from(groups.values());
  }, [messages, optimisticReplies]);

  const submitReply = async (buyerId: string) => {
    if (!listing?.ebayListingId) return;
    if (submittingFor !== null) return;
    const setErr = (msg: string | null) =>
      setReplyErrors((prev) => ({ ...prev, [buyerId]: msg }));
    const parent = [...messages]
      .reverse()
      .find((m) => m.fromBuyer && m.senderUserId === buyerId);
    if (!parent) return setErr("No buyer message to reply to");
    const body = (replyDrafts[buyerId] ?? "").trim();
    if (body.length === 0) return setErr("Reply can't be empty");
    setErr(null);
    setSubmittingFor(buyerId);
    try {
      await replyToEbayListingMessage(listing.ebayListingId, {
        parentMessageId: parent.messageId,
        recipientUserId: buyerId,
        body,
      });
      setOptimisticReplies((prev) => [
        ...prev,
        {
          messageId: `local-${Date.now()}`,
          parentMessageId: parent.messageId,
          body,
          sentAt: new Date().toISOString(),
        },
      ]);
      setReplyDrafts((prev) => ({ ...prev, [buyerId]: "" }));
      onSuccess("Reply sent");
    } catch (err) {
      setErr(err instanceof Error ? err.message : "Failed to send reply");
    } finally {
      setSubmittingFor(null);
    }
  };

  if (!listing) return null;

  return (
    <Modal
      open={open}
      onClose={() => submittingFor === null && onClose()}
      ariaLabel="Buyer messages"
      panelClassName="w-[min(620px,94vw)] max-h-[90vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">
            Buyer messages
          </h2>
          <p className="truncate text-[11px] text-muted-foreground">
            {listing.title}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={submittingFor !== null}
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {loading && (
          <p className="text-center text-xs text-muted-foreground">
            Loading messages…
          </p>
        )}
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {!loading && !error && conversations.length === 0 && (
          <p className="text-center text-xs text-muted-foreground">
            No buyer messages on this listing yet.
          </p>
        )}
        {conversations.map((conv) => (
          <ConversationBlock
            key={conv.buyerUserId}
            conversation={conv}
            draft={replyDrafts[conv.buyerUserId] ?? ""}
            error={replyErrors[conv.buyerUserId] ?? null}
            submitting={submittingFor === conv.buyerUserId}
            disabled={
              submittingFor !== null && submittingFor !== conv.buyerUserId
            }
            onDraftChange={(text) =>
              setReplyDrafts((prev) => ({ ...prev, [conv.buyerUserId]: text }))
            }
            onSubmit={() => void submitReply(conv.buyerUserId)}
          />
        ))}
      </div>
    </Modal>
  );
}

function ConversationBlock({
  conversation,
  draft,
  error,
  submitting,
  disabled,
  onDraftChange,
  onSubmit,
}: {
  conversation: Conversation;
  draft: string;
  error: string | null;
  submitting: boolean;
  disabled: boolean;
  onDraftChange: (text: string) => void;
  onSubmit: () => void;
}) {
  const interleaved = [
    ...conversation.messages,
    ...conversation.optimisticReplies.map((r) => ({
      messageId: r.messageId,
      parentMessageId: r.parentMessageId,
      senderUserId: "you",
      subject: "",
      body: r.body,
      receivedAt: r.sentAt,
      read: true,
      fromBuyer: false,
    })),
  ].sort((a, b) => a.receivedAt.localeCompare(b.receivedAt));

  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/50 p-3">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-foreground">
          {conversation.buyerUserId}
        </span>
        <span className="text-muted-foreground">
          {conversation.messages.filter((m) => m.fromBuyer && !m.read).length}{" "}
          unread
        </span>
      </div>
      <div className="space-y-2">
        {interleaved.map((m) => (
          <MessageBubble key={m.messageId} message={m} />
        ))}
      </div>
      <div className="space-y-1.5">
        <textarea
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          disabled={disabled || submitting}
          placeholder="Reply…"
          rows={2}
          maxLength={2000}
          className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-secondary focus:outline-none disabled:opacity-50"
        />
        <div className="flex items-center justify-between gap-2">
          {error ? (
            <span className="text-[11px] text-destructive">{error}</span>
          ) : (
            <span className="text-[11px] text-muted-foreground">
              {draft.length}/2000
            </span>
          )}
          <button
            type="button"
            onClick={onSubmit}
            disabled={disabled || submitting || draft.trim().length === 0}
            className="inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" />
            {submitting ? "Sending…" : "Send reply"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
}: {
  message:
    | ListingMessage
    | (Omit<ListingMessage, "senderUserId"> & { senderUserId: string });
}) {
  const mine = !message.fromBuyer;
  return (
    <div className={mine ? "flex justify-end" : "flex justify-start"}>
      <div
        className={
          mine
            ? "max-w-[80%] rounded-lg rounded-br-sm bg-secondary px-3 py-2 text-xs text-secondary-foreground"
            : message.read
              ? "max-w-[80%] rounded-lg rounded-bl-sm bg-card px-3 py-2 text-xs text-foreground"
              : "max-w-[80%] rounded-lg rounded-bl-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-foreground"
        }
      >
        {message.subject && message.subject !== "Re:" && (
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide opacity-70">
            {message.subject}
          </div>
        )}
        <div className="whitespace-pre-wrap">{message.body}</div>
        <div className="mt-1 text-right text-[10px] opacity-60">
          {new Date(message.receivedAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
}
